import { State } from 'model/State';
import { fetchWithParamValidation } from 'util/fetchWithParamValidation';
import { getFromHamsClientNonExternalAccountAPI } from 'util/mac/api/account/get';
import { postFromHamsNonExternalClientPublicAPI } from 'util/mac/api/account/post';

export type DcAppParentMapping = {
  appAccountId: number;
  parentAccountId: number | null;
};

export type DcAppParentMappingUploadRequest = {
  source: string;
  appParentMappings: DcAppParentMapping[];
};

export type DcAppParentMappingUploadResult = {
  totalRequested: number;
  successCount: number;
  failureCount: number;
  successfulMappings: DcAppParentMapping[];
  failedMappings: Array<DcAppParentMapping & { errorMessage: string }>;
};

/**
 * Fetches the DC App Parent Mapping for a given partner
 * GET /1.0/public/account/dcAppParentMapping/partner/{partner_id}
 *
 * @param partnerId - The partner ID to fetch mappings for
 * @param state - Redux state object
 * @returns Promise<DcAppParentMapping[]> - Array of app account and parent account mappings
 */
export const fetchDcAppParentMappingByPartner = (partnerId: string, state: State) =>
  fetchWithParamValidation(
    ({ partnerIdValue }) =>
      getFromHamsClientNonExternalAccountAPI(
        `/dcAppParentMapping/partner/${partnerIdValue}`,
        state,
      ),
    {
      partnerIdValue: partnerId,
    },
  );

/**
 * Uploads DC App Parent Mapping data for a given partner
 * POST /public/rest/account/dcAppParentMapping/partner/{partner_id}
 *
 * @param partnerId - The partner ID to upload mappings for
 * @param mappings - Array of app-to-parent account mappings
 * @param state - Redux state object
 * @returns Promise<DcAppParentMappingUploadResult>
 */
export const uploadDcAppParentMappingByPartner = (
  partnerId: string,
  mappings: DcAppParentMapping[],
  state: State,
): Promise<DcAppParentMappingUploadResult> =>
  fetchWithParamValidation(
    ({ partnerIdValue }) =>
      postFromHamsNonExternalClientPublicAPI(
        `/dcAppParentMapping/partner/${partnerIdValue}`,
        state,
        undefined,
        {
          source: 'MAC',
          appParentMappings: mappings,
        } as DcAppParentMappingUploadRequest,
      ) as Promise<DcAppParentMappingUploadResult>,
    {
      partnerIdValue: partnerId,
    },
  ) as Promise<DcAppParentMappingUploadResult>;
